<template>
    <AonModal
        class="patent-list"
        :class="[
            { inline: props.inline },
            { absolute: props.absolute },
            { 'no-action-bar': patentViewerStore.listViewType === 'screener' },
        ]"
        message=""
        col-width="10"
        @close="closePatentList"
    >
        <div class="modal-content display-flex flex-column">
            <div class="patent-list-header display-flex align-items-center">
                <div class="source-display py-md px-md">
                    <div class="basics-wrap display-flex align-items-center">
                        <p v-if="showLitigationTitle" class="inter-font semi-bold text-stone-800">
                            Litigation for:
                        </p>
                        <p v-if="showCountry" class="inter-font semi-bold text-stone-800 mr-xxs">
                            {{ patentViewerStore.ipBasicsObject?.country }} |
                        </p>
                        <p v-if="showBasicsInfo" class="inter-font semi-bold text-stone-800">
                            {{ publicationType }} {{ claimBreadthText }} {{ validityText }}
                        </p>
                    </div>
                    <h4 class="inter-font semi-bold text-stone-800 mt-sm mb-n4">
                        {{ entityTitle }}
                    </h4>
                </div>
            </div>

            <div class="table-holder display-flex flex-column flex-grow-1 h-100 pt-xxs">
                <div
                    v-if="patentViewerStore.listViewType === 'screener'"
                    class="display-flex align-items-start justify-between w-100"
                >
                    <AdvancedFilterBuilder
                        :column-defs="advancedFilterColumns"
                        :prefill-filters="prefillFilters"
                        :loading="loading"
                        @apply-filters="applyFilters"
                    />
                    <AonButton
                        v-if="patentViewerStore.listViewType === 'screener'"
                        :class="{ disabled: noRows || loading }"
                        class="mb-xxl ml-md"
                        label="Export"
                        text-with-icon
                        type="prominent"
                        :icon-options="{ iconStyle: 'fas', iconName: 'fa-file-arrow-down' }"
                        @clicked="exportTablePatents()"
                    />
                </div>
                <div
                    v-else
                    class="root-actions display-flex align-items-center justify-between w-100 mb-md"
                >
                    <template v-if="showQuickFilters">
                        <MoatDropdown
                            class="mr-md"
                            v-model="selectedQuickFilter"
                            :options="quickFilterDropdownOptions"
                            placeholder="Preset Filters"
                            @change="selectQuickFilter"
                        />
                    </template>
                    <AonButton
                        class="mr-md table-button"
                        :class="{ 'active-table-button': activeSideBarType === 'filters' }"
                        label="Filters"
                        text-with-icon
                        type="subtle"
                        :icon-options="{ iconStyle: 'fas', iconName: 'fa-bars-filter' }"
                        @clicked="() => toggleSideBar('filters', gridApi)"
                    />
                    <AonButton
                        class="mr-md table-button"
                        :class="{ 'active-table-button': activeSideBarType === 'columns' }"
                        label="Columns"
                        text-with-icon
                        type="subtle"
                        :icon-options="{ iconStyle: 'fas', iconName: 'fa-objects-column' }"
                        @clicked="() => toggleSideBar('columns', gridApi)"
                    />
                    <div
                        class="clear-filters display-flex align-items-center cursor-pointer p-sm ml-sm"
                        @click="clearFilters"
                    >
                        <font-awesome-icon :icon="`fas fa-xmark`" class="text-moat-600" />
                        <p class="geist-font text-moat-600 bold ml-xs">Clear Filters</p>
                    </div>
                    <div
                        v-if="props.allowPruning"
                        class="actions-parent display-flex align-items-center justify-between w-100"
                        style="min-height: 30px; height: 30px"
                    >
                        <div class="left display-flex align-items-center">
                            <div
                                class="bulk-actions display-flex align-items-center"
                                v-if="selectedRows.length > 0"
                            >
                                <div
                                    class="bulk-action display-flex align-items-center mr-md"
                                    :class="{ disabled: selectedRows.length <= 0 }"
                                    @click="bulkPrune"
                                >
                                    <font-awesome-icon
                                        :icon="`fas fa-seedling`"
                                        class="pruned mt-n1"
                                    />
                                    <p class="geist-font text-success bold ml-sm">Prune</p>
                                </div>
                                <div
                                    class="bulk-action display-flex align-items-center"
                                    :class="{ disabled: selectedRows.length <= 0 }"
                                    @click="bulkUnprune"
                                >
                                    <font-awesome-icon
                                        :icon="`fas fa-seedling`"
                                        class="text-stone-400 mt-n1"
                                    />
                                    <p class="geist-font text-stone-400 bold ml-sm">Unprune</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="flex-grow-1"></div>
                </div>
                <!-- Action Bar -->
                <div
                    v-if="patentViewerStore.listViewType !== 'screener'"
                    :class="{ 'sidebar-open': sideBarOpen }"
                    class="action-bar display-flex align-items-center justify-content-start"
                >
                    <template v-if="props.allowPruning">
                        <div class="total-selected pl-lg" style="min-width: 98px">
                            <p class="text-stone-700">{{ selectedRows.length }} selected</p>
                        </div>
                        <div
                            v-if="lifetimeFeesPruned && parseInt(lifetimeFeesPruned) > 0"
                            class="pl-md ml-md divide-border"
                        >
                            <p class="inter-font text-success display-flex align-items-center">
                                Lifetime fees Pruned:
                                <span class="bold ml-sm">
                                    <font-awesome-icon
                                        :icon="`fas fa-seedling`"
                                        class="pruned mt-n1 mr-xs"
                                    />
                                    ${{ parseInt(lifetimeFeesPruned).toLocaleString() }}
                                </span>
                            </p>
                        </div>
                    </template>
                    <MTAction
                        label="Export"
                        :loading="exportLoading"
                        :icon-options="{
                            iconStyle: 'fas',
                            iconName: 'fa-file-arrow-down',
                            iconColor: 'var(--color-moat-900)',
                        }"
                        :disabled="noRows || loading"
                        @clicked="exportPatents()"
                    />
                </div>
                <!-- End Action Bar -->
                <MoatTable
                    class="w-100 h-100"
                    :class="themeClass"
                    :column-defs="colDefs"
                    :tooltip-interaction="true"
                    :tooltip-mouse-track="true"
                    :tooltip-show-delay="500"
                    tooltip-show-mode="whenTruncated"
                    :side-bar="sideBar"
                    :autoSizeStrategy="sizingStrategy"
                    row-model-type="serverSide"
                    :cache-block-size="100"
                    :max-blocks-in-cache="50"
                    :get-row-id="getRowId"
                    :row-selection="rowSelection"
                    :noRowsOverlayComponent="MTCustomNoRowsOverlay"
                    :noRowsOverlayComponentParams="noRowsOverlayComponentParams"
                    pagination
                    @grid-ready="gridInit"
                    @cell-clicked="onRowClicked"
                    @cell-key-down="onCellKeyDown"
                    @row-selected="addToBulk"
                />
                <div class="total-holder display-flex align-items-center">
                    <p class="total inter-font text-stone-900 mr-md pr-md">
                        Showing patent/s
                        <span class="bold">{{ firstRowOnPage.toLocaleString() }}</span>
                        <span class="bold"> - {{ lastRowOnPage.toLocaleString() }}</span> of
                        <span class="bold">{{ totalPatents.toLocaleString() }}</span> total
                    </p>
                </div>
            </div>
        </div>
    </AonModal>
</template>

<script setup>
import {
    ref,
    inject,
    onBeforeUnmount,
    onMounted,
    computed,
    watch,
    onDeactivated,
    onActivated,
    onBeforeMount,
    nextTick,
} from 'vue'
import { usePatentViewerStore, useEntityStore, usePortfolioManagerStore } from '@/stores'
import {
    getPatentList,
    getLitPatentList,
    getPatentListIPBasics,
    getPatentListInventor,
    getConceptIntersectionPatentList,
    getConceptDirectPatentList,
} from '@/api/patentViewer.js'
import { patentSearchAsync } from '@/api/patents.js'
import {
    submitPruneStatus,
    bulkPrune as helperBulkPrune,
    bulkUnprune as helperBulkUnprune,
    getPruningColumnDef,
} from '@/components/patentViewer/helpers/pruningHelper.js'
import { exportPatents as helperExportPatents } from '@/components/patentViewer/helpers/exportHelper.js'
import { useSignalR } from '@/composables/useSignalR'

import { AgGridVue as MoatTable } from '@ag-grid-community/vue3'
import AdvancedFilterBuilder from '@/components/patentViewer/helpers/advancedFilterBuilder/AdvancedFilterBuilder.vue'
import { dateComparator } from '@/components/moatTable/helpers/compare.js'
import { moatParamBuilder } from '@/components/moatTable/helpers/queryBuilder.js'
import MTCustomNoRowsOverlay from '@/components/moatTable/MTCustomNoRowsOverlay.vue'
import MTAction from '@/components/moatTable/helpers/MTAction.vue'

import { useI18n } from 'vue-i18n'
import { debounce } from 'lodash-es'
import { useAuth } from '@/auth/authPlugin'
import { useFlag, useFlagsStatus, useUnleashContext } from '@unleash/proxy-client-vue'

import MoatDropdown from '@/components/helpers/moatArmory/MoatDropdown.vue'
import { useAgGridSidebar } from '@/composables/useAgGridSidebar.js'
import { useRoute } from 'vue-router'

const showQuickFilters = useFlag('ipAlpha.AAQuickFilters')
const enhancedLitigationData = useFlag('ipAlpha.enhancedLitigationData')
const advancedFiltering = useFlag('ipAlpha.patentListAdvancedFiltering')

const { user } = useAuth()
const logger = inject('logger')
const filters = inject('filters')
const eventBus = inject('eventBus')
const { t } = useI18n()

const patentViewerStore = usePatentViewerStore()
const entityStore = useEntityStore()
const portfolioManagerStore = usePortfolioManagerStore()
const { onQueryCompleted, onQueryError, signalRService } = useSignalR()

const emit = defineEmits(['close-patent-list'])
const props = defineProps({
    inline: {
        type: Boolean,
        default: false,
    },
    absolute: {
        type: Boolean,
        default: false,
    },
    entityPk: {
        type: Number,
        default: null,
    },
    entityOnly: {
        type: Boolean,
        default: false,
    },
    allowPruning: {
        type: Boolean,
        default: false,
    },
    passedFilters: {
        type: Object,
        default: () => ({}),
    },
    allowExport: {
        type: Boolean,
        default: true,
    },
})

const loading = ref(true)
const exportLoading = ref(false)
const gridApi = ref(null)
const themeClass = ref('ag-theme-quartz')
const selectedQuickFilter = ref(null)
const popupParent = ref(null)
const advancedFilterParent = ref(null)
const advancedFilterColumns = ref([])
const quickFilterDropdownOptions = ref([
    {
        label: 'Strongest Patents',
        value: {
            filter: {
                legalStatus: ['Active'],
            },
            sortArray: [
                {
                    sortDir: 'desc',
                    column: 'strengthScore',
                },
            ],
        },
    },
    {
        label: 'Weakest Patents',
        value: {
            filter: {
                legalStatus: ['Active'],
            },
            sortArray: [
                {
                    sortDir: 'asc',
                    column: 'strengthScore',
                },
            ],
        },
    },
    {
        label: 'Narrowest Claims First',
        value: {
            filter: {
                breadth: ['Very Broad', 'Broad', 'Average', 'Narrow'],
            },
            sortArray: [
                {
                    sortDir: 'asc',
                    column: 'claimPercentile',
                },
            ],
        },
    },
    {
        label: 'Expiring Soon',
        value: {
            filter: {
                expirationDateRange: {
                    from: new Date().toISOString().split('T')[0],
                    to: new Date(new Date().setFullYear(new Date().getFullYear() + 5))
                        .toISOString()
                        .split('T')[0],
                },
            },
            sortArray: [
                {
                    sortDir: 'asc',
                    column: 'estimatedExpirationDate',
                },
            ],
        },
    },
    {
        label: 'Litigated',
        value: {
            filter: {
                litigationCount: {
                    greaterThanNum: 0,
                },
            },
        },
    },
])
const sizingStrategy = ref({
    type: 'fitGridWidth',
    defaultMinWidth: 170,
    columnLimits: [
        {
            colId: 'title',
            minWidth: 300,
        },
    ],
})
const tableParams = ref({})
const noRows = ref(false)
const tablePageSize = ref(100)
const totalPatents = ref(0)
const patentsOnPage = ref(0)
const firstRowOnPage = ref(0)
const lastRowOnPage = ref(0)
const selectedPatentIndex = ref(0)
const {
    sideBar,
    activeSideBarType,
    sideBarOpen,
    toggleSideBar,
    injectSidebarHeaders,
    clearFilters: sidebarClearFilters,
} = useAgGridSidebar({
    panels: [
        {
            id: 'columns',
            label: 'Columns',
            width: 275,
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
                suppressRowGroups: true,
                suppressValues: true,
                suppressPivotMode: true,
            },
        },
        {
            id: 'filters',
            label: 'Filters',
            width: 275,
            toolPanel: 'agFiltersToolPanel',
        },
    ],
    injectHeaders: {
        filters: 'Filters',
        columns: 'Columns',
    },
    position: 'left',
})
const previousSortModel = ref([])
const noRowsOverlayComponentParams = ref({
    message: 'There are no patents matching your search criteria.',
})
const selectedRows = ref([])
const feesPruned = ref(0)
const lifetimeFeesPruned = ref(0)

const quickFilterColumns = ref(new Set())
const quickFilterSorts = ref([])
const previousQuickFilter = ref(null)

const route = useRoute()
const seedFilterApplied = ref(false)
const prefillFilters = ref([])

onActivated(() => {
    debounceManager.init()
    if (props.allowPruning) {
        portfolioManagerStore.allowPruning = true
    }
})

onBeforeMount(() => {
    debounceManager.init()
    if (props.allowPruning) {
        quickFilterDropdownOptions.value.push({
            label: 'Prune Eligible',
            value: {
                sortArray: [
                    {
                        sortDir: 'desc',
                        column: 'prune',
                    },
                ],
            },
        })
    }
})

onMounted(() => {
    console.log('PatentList onMounted')
    popupParent.value = document.body
    eventBus.on('next-patent', async () => {
        const nextRow = gridApi.value.getDisplayedRowAtIndex(selectedPatentIndex.value + 1)
        if (nextRow?.data?.patentId) {
            gridApi.value.deselectAll()
            gridApi.value.setFocusedCell(selectedPatentIndex.value + 1, 'patentId')
            await processRowSelection(nextRow.data.publicationPk, nextRow.data)

            selectedPatentIndex.value++
        }
    })
    eventBus.on('previous-patent', async () => {
        const previousRow = gridApi.value.getDisplayedRowAtIndex(selectedPatentIndex.value - 1)
        if (previousRow?.data?.patentId) {
            gridApi.value.deselectAll()
            gridApi.value.setFocusedCell(selectedPatentIndex.value - 1, 'patentId')
            await processRowSelection(previousRow.data.publicationPk, previousRow.data)
            selectedPatentIndex.value--
        }
    })

    eventBus.on('close-patent', () => {
        patentViewerStore.setTargetPatentId(null)
    })

    eventBus.on('prune-patent', async (patentData) => {
        if (!patentData || !patentData.publicationPk) return

        const rowNode = gridApi.value.getRowNode(patentData.patentId)

        if (rowNode) {
            const params = {
                column: { colId: 'prune' },
                data: rowNode.data,
                rowIndex: gridApi.value.getRowNode(patentData.patentId).rowIndex,
            }
            await onRowClicked(params)
        }
    })
})

onBeforeUnmount(() => {
    patentViewerStore.resetState()
    eventBus.off('next-patent')
    eventBus.off('previous-patent')
    eventBus.off('close-patent')
    debounceManager.cancelAll()
    patentViewerStore.strategyTabPatentListObj = null

    // Remove the hidden advanced filter container to avoid memory leaks
    if (advancedFilterParent.value && advancedFilterParent.value.parentNode) {
        advancedFilterParent.value.parentNode.removeChild(advancedFilterParent.value)
        advancedFilterParent.value = null
    }
})

onDeactivated(() => {
    if (gridApi.value && selectedRows.value.length > 0) {
        gridApi.value.deselectAll()
        selectedRows.value = []
    }
    portfolioManagerStore.allowPruning = false
    debounceManager.cancelAll()
    patentViewerStore.strategyTabPatentListObj = null
})

const colDefs = computed(() => {
    const columns = []

    if (props.allowPruning) {
        columns.push(getPruningColumnDef())
    }

    columns.push(
        {
            headerName: 'Patent ID',
            field: 'patentId',
            cellClass: 'link',
            filter: 'agTextColumnFilter',
            filterParams: {
                buttons: ['clear'],
                defaultOption: 'contains',
                filterOptions: ['startsWith', 'equals', 'contains'],
                maxNumConditions: 1,
            },
            minWidth: 200,
        },
        {
            headerName: 'Title',
            field: 'title',
            filter: 'agTextColumnFilter',
            filterParams: {
                buttons: ['clear'],
                defaultOption: 'contains',
                filterOptions: ['startsWith', 'equals', 'contains'],
                maxNumConditions: 1,
            },
        }
    )

    if (props.allowPruning) {
        columns.push(
            {
                headerName: 'Next Fee Due Date',
                colId: 'nextFeeDueDate',
                minWidth: 220,
                valueGetter: (p) => {
                    if (p.data && p.data.nextFeeDueDate) {
                        let date = filters.toUTCString(p.data.nextFeeDueDate)
                        if (date === '1/1/1') {
                            date = '--'
                        }
                        return date
                    }
                    return '--'
                },
                comparator: dateComparator,
                filter: 'agDateColumnFilter',
                filterParams: {
                    suppressAndOrCondition: true,
                    buttons: ['clear'],
                    defaultOption:
                        patentViewerStore.listViewType === 'screener' ? 'greaterThan' : 'inRange',
                    filterOptions:
                        patentViewerStore.listViewType === 'screener'
                            ? ['greaterThan', 'lessThan']
                            : ['inRange', 'greaterThan', 'lessThan'],
                    maxNumConditions: 1,
                },
                sort: 'asc',
                suppressMenu: true,
            },
            {
                headerName: 'Remaining Fees',
                field: 'remainingFees',
                minWidth: 180,
                valueFormatter: (p) => {
                    if (p.data && p.data.remainingFees !== undefined) {
                        return `$${p.data.remainingFees.toLocaleString()}`
                    }
                    return '--'
                },
                filter: 'agNumberColumnFilter',
                filterParams: {
                    buttons: ['clear'],
                    defaultOption: 'equals',
                    filterOptions: ['equals', 'lessThan', 'greaterThan'],
                    maxNumConditions: 1,
                },
            },
            {
                headerName: 'Products',
                field: 'products',
                filter: 'agTextColumnFilter',
                filterParams: {
                    buttons: ['clear'],
                    defaultOption: 'contains',
                    filterOptions: ['startsWith', 'equals', 'contains'],
                    maxNumConditions: 1,
                },
            }
        )
    }

    columns.push(
        {
            field: 'entities',
            headerName: 'Assignee',
            minWidth: 300,
            filter: 'agTextColumnFilter',
            filterParams: {
                buttons: ['clear'],
                defaultOption: 'contains',
                filterOptions: ['startsWith', 'equals', 'contains'],
                maxNumConditions: 1,
            },
        },
        {
            field: 'ultimateEntities',
            headerName: 'Ultimate Assignee',
            minWidth: 300,
            filter: 'agTextColumnFilter',
            filterParams: {
                buttons: ['clear'],
                defaultOption: 'contains',
                filterOptions: ['startsWith', 'equals', 'contains'],
                maxNumConditions: 1,
            },
        },
        {
            headerName: 'Patent Type',
            field: 'patentType',
            minWidth: 140,
            filter: 'agSetColumnFilter',
            filterParams: {
                values: ['A', 'G'],
                valueFormatter: (p) => {
                    if (p.value) {
                        return p.value === 'A' ? 'Application' : 'Grant'
                    }
                },
            },
        },
        // Only include this column if not using advanced filtering
        ...(patentViewerStore.listViewType !== 'screener'
            ? [
                  {
                      headerName: 'Assignee Country',
                      field: 'assigneeCountry',
                      minWidth: 130,
                      filter: 'agTextColumnFilter',
                      filterParams: {
                          buttons: ['clear'],
                          defaultOption: 'contains',
                          filterOptions: ['startsWith', 'equals', 'contains'],
                          maxNumConditions: 1,
                      },
                  },
              ]
            : []),
        {
            headerName: 'Validity',
            field: 'validity',
            minWidth: 110,
            filter: 'agSetColumnFilter',
            filterParams: {
                values: ['Low', 'Medium', 'High', 'Not Ranked'],
            },
        },
        {
            headerName: 'Jurisdiction',
            field: 'jurisdiction',
            minWidth: 130,
            filter: 'agTextColumnFilter',
            filterParams: {
                buttons: ['clear'],
                defaultOption: 'contains',
                filterOptions: ['startsWith', 'equals', 'contains'],
                maxNumConditions: 1,
            },
        },
        {
            headerName: 'Legal Status',
            field: 'legalStatus',
            minWidth: 140,
            filter: 'agSetColumnFilter',
            filterParams: {
                values: ['active', 'inactive'],
                valueFormatter: (p) => {
                    if (p.value) {
                        return p.value === 'active' ? 'Active' : 'Inactive'
                    }
                },
            },
        }
    )

    if (enhancedLitigationData.value) {
        columns.push({
            headerName: 'Litigation Count',
            field: 'litigationCount',
            minWidth: 130,
            valueFormatter: (p) => {
                if (p.data) {
                    return p.data.litigationCount.toLocaleString()
                }
            },
            filter: 'agNumberColumnFilter',
            filterParams: {
                buttons: ['clear'],
                defaultOption: 'equals',
                filterOptions: ['greaterThan', 'equals', 'lessThan'],
                maxNumConditions: 1,
            },
            cellDataType: 'number',
        })
    }

    columns.push(
        // Filing Date (visible display)
        {
            headerName: 'Filing Date',
            colId: 'filingDateDisplay',
            field: 'filingDate',
            cellDataType: 'dateString',
            filter: 'agDateColumnFilter',
            suppressMenu: true,
            valueGetter: ({ data }) => {
                const raw = data?.filingDate
                if (!raw || raw.startsWith('0001-01-01')) return null
                return raw.split('T')[0]
            },
            valueFormatter: ({ value }) => {
                if (!value) return '--'
                return new Date(value).toLocaleDateString('en-US')
            },
            comparator: dateComparator,
        },
        // Filing Date (hidden filter)
        ...(advancedFiltering.value && patentViewerStore.listViewType === 'screener'
            ? [
                  {
                      headerName: 'Filing Date',
                      colId: 'filingDate',
                      field: 'filingDate',
                      hide: true,
                      cellDataType: 'dateString',
                      filter: 'agDateColumnFilter',
                      comparator: dateComparator,
                      valueGetter: ({ data }) => {
                          const raw = data?.filingDate
                          if (!raw || raw.startsWith('0001-01-01')) return null
                          return raw.split('T')[0]
                      },
                      filterParams: {
                          suppressAndOrCondition: true,
                          buttons: ['clear'],
                          defaultOption:
                              patentViewerStore.listViewType === 'screener'
                                  ? 'greaterThan'
                                  : 'inRange',
                          filterOptions:
                              patentViewerStore.listViewType === 'screener'
                                  ? ['greaterThan', 'lessThan']
                                  : ['inRange', 'greaterThan', 'lessThan'],
                          maxNumConditions: 1,
                      },
                  },
              ]
            : []),
        // Publication Date (visible display)
        {
            headerName: 'Publication Date',
            colId: 'publicationDateDisplay',
            field: 'publicationDate',
            cellDataType: 'dateString',
            filter: 'agDateColumnFilter',
            suppressMenu: true,
            valueGetter: ({ data }) => {
                const raw = data?.publicationDate
                if (!raw || raw.startsWith('0001-01-01')) return null
                return raw.split('T')[0]
            },
            valueFormatter: ({ value }) => {
                if (!value) return '--'
                return new Date(value).toLocaleDateString('en-US')
            },
            comparator: dateComparator,
        },
        // Publication Date (hidden filter)
        ...(advancedFiltering.value && patentViewerStore.listViewType === 'screener'
            ? [
                  {
                      headerName: 'Publication Date',
                      colId: 'publicationDate',
                      field: 'publicationDate',
                      hide: true,
                      cellDataType: 'dateString',
                      filter: 'agDateColumnFilter',
                      comparator: dateComparator,
                      valueGetter: ({ data }) => {
                          const raw = data?.publicationDate
                          if (!raw || raw.startsWith('0001-01-01')) return null
                          return raw.split('T')[0]
                      },
                      filterParams: {
                          suppressAndOrCondition: true,
                          buttons: ['clear'],
                          defaultOption:
                              patentViewerStore.listViewType === 'screener'
                                  ? 'greaterThan'
                                  : 'inRange',
                          filterOptions:
                              patentViewerStore.listViewType === 'screener'
                                  ? ['greaterThan', 'lessThan']
                                  : ['inRange', 'greaterThan', 'lessThan'],
                          maxNumConditions: 1,
                      },
                  },
              ]
            : []),
        // Priority Date display + hidden
        {
            headerName: 'Priority Date',
            colId: 'priorityDateDisplay',
            field: 'priorityDate',
            cellDataType: 'dateString',
            filter: 'agDateColumnFilter',
            suppressMenu: true,
            valueGetter: ({ data }) => {
                const raw = data?.priorityDate
                if (!raw || raw.startsWith('0001-01-01')) return null
                return raw.split('T')[0]
            },
            valueFormatter: ({ value }) => {
                if (!value) return '--'
                return new Date(value).toLocaleDateString('en-US')
            },
            comparator: dateComparator,
        },
        ...(advancedFiltering.value && patentViewerStore.listViewType === 'screener'
            ? [
                  {
                      headerName: 'Priority Date',
                      colId: 'priorityDate',
                      field: 'priorityDate',
                      hide: true,
                      cellDataType: 'dateString',
                      filter: 'agDateColumnFilter',
                      comparator: dateComparator,
                      valueGetter: ({ data }) => {
                          const raw = data?.priorityDate
                          if (!raw || raw.startsWith('0001-01-01')) return null
                          return raw.split('T')[0]
                      },
                      filterParams: {
                          suppressAndOrCondition: true,
                          buttons: ['clear'],
                          defaultOption:
                              patentViewerStore.listViewType === 'screener'
                                  ? 'greaterThan'
                                  : 'inRange',
                          filterOptions:
                              patentViewerStore.listViewType === 'screener'
                                  ? ['greaterThan', 'lessThan']
                                  : ['inRange', 'greaterThan', 'lessThan'],
                          maxNumConditions: 1,
                      },
                  },
              ]
            : []),
        // Estimated Expiration Date display + hidden
        {
            headerName: 'Est. Expiration Date',
            colId: 'estimatedExpirationDateDisplay',
            field: 'estimatedExpirationDate',
            cellDataType: 'dateString',
            filter: 'agDateColumnFilter',
            suppressMenu: true,
            valueGetter: ({ data }) => {
                const raw = data?.estimatedExpirationDate
                if (!raw || raw.startsWith('0001-01-01')) return null
                return raw.split('T')[0]
            },
            valueFormatter: ({ value }) => {
                if (!value) return '--'
                return new Date(value).toLocaleDateString('en-US')
            },
            comparator: dateComparator,
        },
        ...(advancedFiltering.value && patentViewerStore.listViewType === 'screener'
            ? [
                  {
                      headerName: 'Est. Expiration Date',
                      colId: 'estimatedExpirationDate',
                      field: 'estimatedExpirationDate',
                      hide: true,
                      cellDataType: 'dateString',
                      filter: 'agDateColumnFilter',
                      comparator: dateComparator,
                      valueGetter: ({ data }) => {
                          const raw = data?.estimatedExpirationDate
                          if (!raw || raw.startsWith('0001-01-01')) return null
                          return raw.split('T')[0]
                      },
                      filterParams: {
                          suppressAndOrCondition: true,
                          buttons: ['clear'],
                          defaultOption:
                              patentViewerStore.listViewType === 'screener'
                                  ? 'greaterThan'
                                  : 'inRange',
                          filterOptions:
                              patentViewerStore.listViewType === 'screener'
                                  ? ['greaterThan', 'lessThan']
                                  : ['inRange', 'greaterThan', 'lessThan'],
                          maxNumConditions: 1,
                      },
                  },
              ]
            : []),
        {
            headerName: 'Application Number',
            field: 'applicationNumber',
            filter: 'agTextColumnFilter',
            filterParams: {
                buttons: ['clear'],
                defaultOption: 'contains',
                filterOptions: ['startsWith', 'equals', 'contains'],
                maxNumConditions: 1,
            },
        },
        {
            headerName: 'Claim Breadth',
            field: 'claimBreadth',
            filter: 'agSetColumnFilter',
            filterParams: {
                values: ['Very Broad', 'Broad', 'Average', 'Narrow', 'Not Scored'],
            },
            sortingOrder: ['asc', 'desc'],
            // This tells AG Grid to use a custom sort
            comparator: () => 0,
        },
        {
            headerName: 'Claim Percentile Rank',
            field: 'claimPercentile',
            valueFormatter: (p) => {
                if (p.data && p.data.claimPercentile !== 0) {
                    return `${(p.data.claimPercentile * 100).toFixed(2)} %`
                }
                return ''
            },
            filter: 'agNumberColumnFilter',
            filterParams: {
                buttons: ['clear'],
                defaultOption: 'equals',
                filterOptions: ['equals', 'lessThan', 'greaterThan'],
                maxNumConditions: 1,
            },
            hide: true,
            cellDataType: 'number',
        },
        ...(patentViewerStore.listViewType !== 'screener'
            ? [
                  {
                      headerName: 'Inventors',
                      field: 'inventors',
                      filter: 'agTextColumnFilter',
                      filterParams: {
                          buttons: ['clear'],
                          defaultOption: 'contains',
                          filterOptions: ['startsWith', 'equals', 'contains'],
                          maxNumConditions: 1,
                      },
                  },
              ]
            : []),
        {
            headerName: 'Strength Score',
            field: 'strengthScore',
            valueFormatter: (p) => {
                if (p.data && p.data.strengthScore !== 0) {
                    return `${p.data.strengthScore.toFixed(2)}`
                }
                return 0
            },
            filter: 'agNumberColumnFilter',
            filterParams: {
                buttons: ['clear'],
                defaultOption: 'equals',
                filterOptions: ['equals', 'lessThan', 'greaterThan'],
                maxNumConditions: 1,
            },
            hide: true,
            suppressColumnsToolPanel: true,
            lockVisible: true,
        }
    )

    if (advancedFiltering.value && patentViewerStore.listViewType === 'screener') {
        columns.push(
            {
                field: 'abstract',
                headerName: 'Abstract',
                hide: true,
                filter: 'agTextColumnFilter',
                filterParams: {
                    buttons: ['clear'],
                    defaultOption: 'contains',
                    filterOptions: ['startsWith', 'equals', 'contains'],
                },
            },
            {
                field: 'claims',
                headerName: 'Claims',
                hide: true,
                width: 100,
                filter: 'agTextColumnFilter',
                filterParams: {
                    buttons: ['clear'],
                    defaultOption: 'contains',
                    filterOptions: ['startsWith', 'equals', 'contains'],
                },
            }
        )
    }

    return columns
})

const rowSelection = computed(() => {
    if (props.allowPruning) {
        return {
            mode: 'multiRow',
            checkboxes: true,
            suppressRowDeselection: false,
            headerCheckbox: false,
        }
    } else {
        return {
            mode: 'single',
            checkboxes: false,
            enableClickSelection: true,
            suppressRowDeselection: false,
        }
    }
})

const showLitigationTitle = computed(() => patentViewerStore.listViewType === 'lit')

const showCountry = computed(() => patentViewerStore.ipBasicsObject?.country)

const showBasicsInfo = computed(() => patentViewerStore.ipBasicsObject)

const publicationType = computed(() => {
    const type = patentViewerStore.ipBasicsObject?.publicationType
    return type === 'A' ? 'Applications' : type === 'G' ? 'Grants' : 'Total'
})

const claimBreadthText = computed(() => {
    const breadth = patentViewerStore.ipBasicsObject?.claimBreadth
    return breadth ? ` | ${breadth}` : ''
})

const validityText = computed(() => {
    const validity = patentViewerStore.ipBasicsObject?.validity
    return validity ? ` - ${validity}` : ''
})

const entityTitle = computed(() => {
    const tech = patentViewerStore.technologyName
    return `${patentViewerStore.entityName}${tech ? ` - ${tech}` : ''}`
})

watch(
    () => props.passedFilters,
    (newVal, oldVal) => {
        if (newVal !== oldVal && gridApi.value) {
            applyPassedFilters(newVal)
        }
    }
)

watch(
    () => patentsOnPage.value,
    (newValue) => {
        if (gridApi.value) {
            const showResetButton = newValue === 0 && totalPatents.value !== 0
            const updatedMessage = buildNoRowsMessage(showResetButton)
            if (showResetButton) {
                setTimeout(() => {
                    gridApi.value.setGridOption('noRowsOverlayComponentParams', {
                        message: updatedMessage,
                        showResetFilterButton: showResetButton,
                        resetFilters: () => {
                            gridApi.value.setFilterModel(null)
                            gridApi.value.hideOverlay()
                            gridApi.value.refreshServerSide({ purge: true })
                        },
                    })
                    gridApi.value.showNoRowsOverlay()
                }, 100)
            } else {
                gridApi.value.hideOverlay()
            }
        }
    }
)

watch(
    () => patentViewerStore.listViewType,
    (newValue, oldValue) => {
        if (newValue === 'screener') {
            enableAdvancedFilter()
        } else if (oldValue === 'screener' && newValue !== 'screener') {
            disableAdvancedFilter()
        }
    }
)

const gridInit = (params) => {
    gridApi.value = params.api
    setupServerSideData()
    gridApi.value.closeToolPanel()
    injectSidebarHeaders()

    if (props.allowPruning) {
        gridApi.value.setFilterModel({
            nextFeeDueDate: {
                filterType: 'date',
                type: 'inRange',
                dateFrom: formatDate(portfolioManagerStore.dateRange[0]),
                dateTo: formatDate(portfolioManagerStore.dateRange[1]),
            },
        })
    }

    if (props.passedFilters && Object.keys(props.passedFilters).length > 0) {
        applyPassedFilters(props.passedFilters)
    }

    enableAdvancedFilter()

    // Delay seed string filter application to ensure grid is fully ready
    nextTick(() => {
        setTimeout(() => {
            applySeedStringFilter()
        }, 500)
    })
}

const enableAdvancedFilter = () => {
    if (gridApi.value && patentViewerStore.listViewType === 'screener') {
        // Render the Advanced Filter component into a hidden DOM node so that
        // the feature is enabled but the UI is not visible in the grid.
        gridApi.value.setGridOption('enableAdvancedFilter', true)
        gridApi.value.setGridOption('includeHiddenColumnsInAdvancedFilter', true)
        gridApi.value.setGridOption('popupParent', popupParent.value)

        if (!advancedFilterParent.value) {
            const hiddenDiv = document.createElement('div')
            hiddenDiv.style.display = 'none'
            document.body.appendChild(hiddenDiv)
            advancedFilterParent.value = hiddenDiv
        }

        gridApi.value.setGridOption('advancedFilterParent', advancedFilterParent.value)

        // Explicitly suppress header filter buttons when advanced filtering is enabled
        // This prevents race conditions where individual column filters might still show
        const columnDefs = gridApi.value.getColumnDefs()
        columnDefs.forEach((colDef) => {
            if (colDef.filter && colDef.filter !== false) {
                colDef.suppressHeaderFilterButton = true
            }
        })
        gridApi.value.setGridOption('columnDefs', columnDefs)

        // Exclude non-filterable display-only columns (ending with 'Display')
        advancedFilterColumns.value = gridApi.value
            .getColumnDefs()
            .filter(
                (c) =>
                    c.filter !== false &&
                    !(c.colId && c.colId.endsWith('Display')) &&
                    c.colId !== 'claimPercentile' &&
                    c.colId !== 'assigneeCountry' &&
                    c.colId !== 'strengthScore' &&
                    c.colId !== 'inventors'
            )
    }
}

const disableAdvancedFilter = () => {
    if (gridApi.value && patentViewerStore.listViewType !== 'screener') {
        // Disable advanced filtering
        gridApi.value.setGridOption('enableAdvancedFilter', false)

        // Restore individual column header filter buttons
        const columnDefs = gridApi.value.getColumnDefs()
        columnDefs.forEach((colDef) => {
            if (colDef.filter && colDef.filter !== false) {
                colDef.suppressHeaderFilterButton = false
            }
        })
        gridApi.value.setGridOption('columnDefs', columnDefs)

        // Clear advanced filter columns
        advancedFilterColumns.value = []
    }
}

const buildNoRowsMessage = (filtersApplied) => {
    const message = patentViewerStore.ipBasicsObject
        ? `There doesn't seem to be any patents for - ${patentViewerStore.entityName} - ${patentViewerStore.ipBasicsObject.country} ${
              patentViewerStore.ipBasicsObject.publicationType === 'A'
                  ? 'Applications'
                  : patentViewerStore.ipBasicsObject.publicationType === 'G'
                    ? 'Grants'
                    : 'Total'
          } ${patentViewerStore.ipBasicsObject.claimBreadth ? ' | ' + '-' + patentViewerStore.ipBasicsObject.claimBreadth : ''} ${patentViewerStore.ipBasicsObject.validity ? patentViewerStore.ipBasicsObject.validity : ''}`
        : `There are no patents matching your search criteria.`

    if (filtersApplied) {
        return message + ', with the current filters applied.'
    }

    return message
}

const getRowId = (params) => {
    return params.data.patentId
}

const setupServerSideData = async () => {
    const datasource = createServerSideDatasource()
    gridApi.value.setGridOption('serverSideDatasource', datasource)

    // Only trigger initial data fetch if not in screener mode
    if (patentViewerStore.listViewType !== 'screener') {
        gridApi.value.refreshServerSide({ purge: true })
    } else {
        // For screener mode, show empty state initially
        gridApi.value.showNoRowsOverlay()
        noRows.value = true
    }
}

const createServerSideDatasource = () => {
    let previousFilterModel = null

    return {
        getRows: (params) => {
            // Determine which filter model AG Grid provided (standard vs. advanced)
            const effectiveModel =
                params.request.advancedFilterModel || params.request.filterModel || {}

            // In screener mode we only hit the backend when *some* filter exists
            if (
                patentViewerStore.listViewType === 'screener' &&
                (!effectiveModel || Object.keys(effectiveModel).length === 0)
            ) {
                // Return an empty result set and mark the grid as having no rows so that
                // UI elements (e.g. Export button) can react appropriately.  This path is
                // hit when the Screener table is first shown with no filters applied.
                params.success({ rowData: [] })
                noRows.value = true
                return
            }

            // Normalise so the rest of the pipeline always sees filterModel
            params.request.filterModel = effectiveModel

            debounceManager.cancelAll()

            const currentFilterModel = effectiveModel

            params.request.filterChanged =
                JSON.stringify(previousFilterModel) !== JSON.stringify(currentFilterModel)
            previousFilterModel = JSON.parse(JSON.stringify(currentFilterModel))

            debounceManager.dataFetch(params, async (p) => {
                let response
                if (patentViewerStore.strategyTabPatentListObj) {
                    response = await onGetPatentList(
                        params.request,
                        patentViewerStore.strategyTabPatentListObj
                    )
                } else if (patentViewerStore.ipBasicsObject) {
                    response = await onGetPatentListIPBasics(params.request, props.entityPk)
                } else if (props.entityOnly) {
                    response = await onGetPatentListIPBasics(params.request, props.entityPk)
                    patentViewerStore.entityPkList = [props.entityPk]
                } else if (patentViewerStore.inventorPk) {
                    response = await onGetPatentListInventor(params.request)
                } else {
                    response = await onGetPatentList(params.request)
                }

                if (response && response.success) {
                    // Keep the local noRows flag in sync with the latest results so that
                    // downstream logic (e.g. disabling the Export button) works reliably
                    // after every data fetch or advanced-filter refresh.
                    if (response.rows.length === 0) {
                        gridApi.value.showNoRowsOverlay()
                        noRows.value = true
                    } else {
                        gridApi.value.hideOverlay()
                        noRows.value = false
                    }
                    params.success({ rowData: response.rows })
                } else {
                    params.fail()
                }
            })
        },
    }
}

const handleAsyncPatentSearch = async (tableParams) => {
    try {
        await signalRService.ensureConnection()
        const searchResponse = await patentSearchAsync(tableParams)
        const queryId = searchResponse.data.queryId

        if (!queryId) {
            throw new Error('No query ID returned from async search')
        }

        // Create a promise that resolves when SignalR delivers results
        return new Promise((resolve, reject) => {
            let queryCompletedCleanup = null
            let queryErrorCleanup = null

            // Set up timeout to avoid hanging indefinitely
            const timeout = setTimeout(() => {
                if (queryCompletedCleanup) queryCompletedCleanup()
                if (queryErrorCleanup) queryErrorCleanup()
                reject(new Error('Patent search timeout - no response received'))
            }, 90000)

            // Listen for query completion
            queryCompletedCleanup = onQueryCompleted((payload) => {
                if (payload.queryId === queryId) {
                    clearTimeout(timeout)
                    if (queryCompletedCleanup) queryCompletedCleanup()
                    if (queryErrorCleanup) queryErrorCleanup()
                    resolve({
                        data: payload.results,
                    })
                }
            })

            // Listen for query errors
            queryErrorCleanup = onQueryError((payload) => {
                if (payload.queryId === queryId) {
                    clearTimeout(timeout)
                    if (queryCompletedCleanup) queryCompletedCleanup()
                    if (queryErrorCleanup) queryErrorCleanup()
                    logger.error('PatentList received error:', payload)
                    reject(new Error(payload.message || payload.error || 'Patent search failed'))
                }
            })
        })
    } catch (error) {
        logger.error('Error in async patent search:', error)
        throw error
    }
}

const onGetPatentList = async (paramsFromTable, strategyTabPatentListObj = null) => {
    const fetchFunction = async (tableParams) => {
        let nodeParams = null
        if (strategyTabPatentListObj) {
            nodeParams = strategyTabPatentListObj
        } else {
            nodeParams = {
                nodeIds: patentViewerStore.nodeIdList,
                entityPks: props.entityPk ? [props.entityPk] : patentViewerStore.entityPkList,
            }
        }

        switch (patentViewerStore.listViewType) {
            case 'lit':
                return await getLitPatentList(patentViewerStore.nodeIdList[0], tableParams)

            case 'conceptIntersection':
                return await getConceptIntersectionPatentList(
                    patentViewerStore.nodeIdList,
                    patentViewerStore.entityPkList,
                    tableParams
                )

            case 'conceptDirect':
                return getConceptDirectPatentList(
                    patentViewerStore.nodeIdList,
                    patentViewerStore.entityPkList,
                    tableParams
                )

            case 'screener':
                return await handleAsyncPatentSearch(tableParams)

            default:
                return await getPatentList(nodeParams, tableParams)
        }
    }

    return handlePatentListRequest(paramsFromTable, fetchFunction)
}

const onGetPatentListIPBasics = async (paramsFromTable, entityPk = null) => {
    const fetchFunction = async (tableParams) => {
        return await getPatentListIPBasics(
            entityPk ? entityPk : patentViewerStore.ipBasicsEntityPK,
            entityPk ? {} : patentViewerStore.ipBasicsObject,
            tableParams
        )
    }

    return handlePatentListRequest(paramsFromTable, fetchFunction)
}

const onGetPatentListInventor = async (paramsFromTable) => {
    const fetchFunction = async (tableParams) => {
        return await getPatentListInventor(
            {
                inventorPk: patentViewerStore.inventorPk,
                entityPk: patentViewerStore.entityPk,
            },
            tableParams
        )
    }
    return handlePatentListRequest(paramsFromTable, fetchFunction)
}

const handlePatentListRequest = async (paramsFromTable, fetchFunction) => {
    loading.value = true

    // Replace any *Display sort columns with their real counterparts
    const displayToDataMap = {
        filingDateDisplay: 'filingDate',
        publicationDateDisplay: 'publicationDate',
        priorityDateDisplay: 'priorityDate',
        estimatedExpirationDateDisplay: 'estimatedExpirationDate',
    }

    const currentSortModel = (paramsFromTable.sortModel || []).map((s) => ({
        ...s,
        colId: displayToDataMap[s.colId] || s.colId,
    }))

    const currentFilterModel = paramsFromTable.filterModel || {}

    if (currentSortModel.some((sort) => sort.colId === 'claimBreadth')) {
        // Find the claimBreadth sort in the model
        const claimBreadthSort = currentSortModel.find((sort) => sort.colId === 'claimBreadth')

        // Add a custom parameter to indicate how to sort claimBreadth
        tableParams.value.customClaimBreadthSort = claimBreadthSort.sort
    }

    const sortChanged = JSON.stringify(previousSortModel.value) !== JSON.stringify(currentSortModel)
    const filterChanged = paramsFromTable.filterChanged || false

    selectedRows.value = []

    if (sortChanged || filterChanged) {
        previousSortModel.value = [...currentSortModel]
    }

    try {
        const filterModel = currentFilterModel || gridApi.value.getFilterModel()

        const adjustedParams = { ...paramsFromTable, sortModel: currentSortModel, filterModel }

        // Calculate page number for moatParamBuilder (0-indexed for pages after first)
        const pageForBuilder = Math.floor((paramsFromTable.startRow || 0) / tablePageSize.value)

        tableParams.value = moatParamBuilder(
            adjustedParams,
            { page_size: tablePageSize.value, last_row_num: 0, page: pageForBuilder },
            null
        )
        patentsOnPage.value = 0
        firstRowOnPage.value = 0
        lastRowOnPage.value = 0
        totalPatents.value = 0
        feesPruned.value = 0
        lifetimeFeesPruned.value = 0

        const data = await fetchFunction(tableParams.value)

        console.log('data', data.data)
        totalPatents.value = data.data.rowCount
        patentsOnPage.value = data.data.lastRowOnPage + 1 - data.data.firstRowOnPage
        firstRowOnPage.value =
            data.data.firstRowOnPage === 1 && data.data.lastRowOnPage === 0
                ? 0
                : data.data.firstRowOnPage
        lastRowOnPage.value = data.data.lastRowOnPage
        feesPruned.value = data.data.feesPruned
        lifetimeFeesPruned.value = data.data.lifetimeFeesPruned

        if (data.data.results.length === 0) {
            gridApi.value.showNoRowsOverlay()
            noRows.value = true
        } else {
            gridApi.value.hideOverlay()
            noRows.value = false
        }
        // Remove the manual page increment - AG Grid handles this automatically
        return {
            success: true,
            rows: data.data.results,
        }
    } catch (error) {
        logger.error(error)
        return {
            success: false,
        }
    } finally {
        loading.value = false
    }
}

const processRowSelection = async (patentId, rowData) => {
    if (patentViewerStore.patentExistsInCache(patentId)) {
        await patentViewerStore.setTargetPatent(patentId)
    } else {
        rowData.isRowData = true
        await patentViewerStore.setTargetPatentId(patentId)
        updateTargetPatentData(patentId, rowData)
    }

    eventBus.emit('patent-changed', patentId)
}

const addToBulk = (params) => {
    selectedRows.value = []
    const selectedNodes = gridApi.value.getSelectedNodes()
    if (selectedNodes && selectedNodes.length > 0) {
        selectedNodes.forEach((node) => {
            if (node.data) {
                selectedRows.value.push(node.data)
            }
        })
    }
}

const onRowClicked = async (params) => {
    if (params.column.colId === 'prune') {
        params.data.prune = !params.data.prune

        const rowNode = gridApi.value.getRowNode(params.data.patentId)
        if (rowNode) {
            rowNode.setData({ ...params.data })
        }

        params.data.prune
            ? (feesPruned.value += params.data.nextFeeDue)
            : (feesPruned.value -= params.data.nextFeeDue)

        params.data.prune
            ? (lifetimeFeesPruned.value += params.data.remainingFees)
            : (lifetimeFeesPruned.value -= params.data.remainingFees)

        if (patentViewerStore.patentDetails) {
            patentViewerStore.patentDetails.prune = params.data.prune

            selectedPatentIndex.value = params.rowIndex
            if (params?.data?.patentId) {
                await processRowSelection(
                    params.data.publicationPk || params.data.patentId,
                    params.data
                )
            }
        }

        await submitPruneStatus(params.data, [], null, logger)
    } else {
        selectedPatentIndex.value = params.rowIndex
        if (params?.data?.patentId) {
            await processRowSelection(
                params.data.publicationPk || params.data.patentId,
                params.data
            )
        }
    }
}

const bulkPrune = () => handleBulkPruneAction('prune')
const bulkUnprune = () => handleBulkPruneAction('unprune')

const handleBulkPruneAction = async (action) => {
    const isPruneAction = action === 'prune'
    const expectedCurrentState = !isPruneAction // false for prune, true for unprune
    const errorMessage = isPruneAction
        ? 'Some patents are already pruned. Please select only unpruned patents.'
        : 'Some patents are not pruned. Please select only pruned patents.'

    if (selectedRows.value.every((row) => row.prune === expectedCurrentState)) {
        // Calculate fee adjustments
        const feesTotalAdjustment = selectedRows.value.reduce(
            (total, row) => total + (row.nextFeeDue || 0),
            0
        )
        const lifetimeFeesTotalAdjustment = selectedRows.value.reduce(
            (total, row) => total + (row.remainingFees || 0),
            0
        )

        // Update fee totals
        feesPruned.value += isPruneAction ? feesTotalAdjustment : -feesTotalAdjustment
        lifetimeFeesPruned.value += isPruneAction
            ? lifetimeFeesTotalAdjustment
            : -lifetimeFeesTotalAdjustment

        if (patentViewerStore.patentDetails) {
            const matchingRow = selectedRows.value.find(
                (row) => row.publicationPk === patentViewerStore.patentDetails.publicationPk
            )

            // If there's a match, update the prune status in the patent details
            if (matchingRow) {
                patentViewerStore.patentDetails.prune = isPruneAction
            }
        }

        await (isPruneAction
            ? helperBulkPrune(gridApi.value, selectedRows.value, logger)
            : helperBulkUnprune(gridApi.value, selectedRows.value, logger))

        // Clear selection
        gridApi.value.deselectAll()
        selectedRows.value = []
    } else {
        eventBus.emit('snacktime', {
            type: 'caution',
            message: errorMessage,
            contextual: false,
        })
    }
}

const updateTargetPatentData = (patentId, rowData) => {
    if (patentId) {
        debounceManager.patentUpdate(patentId, rowData, async (id, data) => {
            patentViewerStore.setCachedPatentRowDetail(id, data)
            await patentViewerStore.fetchPatentDetails(id)
        })
    }
}

const onCellKeyDown = async (params) => {
    selectedPatentIndex.value = params.rowIndex
    let newPatentId = null
    let row = null
    if (params.event.key === 'ArrowUp') {
        row = gridApi.value.getDisplayedRowAtIndex(selectedPatentIndex.value - 1)
        if (row?.data?.patentId) {
            newPatentId = row.data.publicationPk
        }
    } else if (params.event.key === 'ArrowDown') {
        row = gridApi.value.getDisplayedRowAtIndex(selectedPatentIndex.value + 1)
        if (row?.data?.patentId) {
            newPatentId = row.data.publicationPk
        }
    }
    await processRowSelection(newPatentId, row.data)
}

const selectQuickFilter = (models) => {
    // Get current filters and create new filter model
    const currentFilterModel = gridApi.value.getFilterModel() || {}
    const newFilterModel = createFilterModel(models.value.filter)
    const mergedFilterModel = { ...currentFilterModel }

    // Remove previous quick filter columns if they exist
    if (previousQuickFilter.value) {
        const previousFilterModel = createFilterModel(previousQuickFilter.value.filter)
        Object.keys(previousFilterModel).forEach((column) => {
            mergedFilterModel[column] = null
        })
        gridApi.value.setFilterModel(mergedFilterModel)
    }

    // Update tracking and add new quick filter columns
    quickFilterColumns.value = new Set(Object.keys(newFilterModel))
    quickFilterSorts.value = models.value.sortArray || []
    previousQuickFilter.value = models.value

    Object.keys(newFilterModel).forEach((column) => {
        mergedFilterModel[column] = newFilterModel[column]
    })

    // Apply filters and sorts
    const sortModel = createSortModel(quickFilterSorts.value)
    applyFiltersAndRefresh(mergedFilterModel, sortModel)
}

const applyPassedFilters = (filters, sortArray) => {
    // Get current filters and create new filter model
    const currentFilterModel = gridApi.value.getFilterModel() || {}
    const newFilterModel = createFilterModel(filters)
    const mergedFilterModel = { ...currentFilterModel }

    // Add new filters while preserving quick filter columns
    Object.keys(newFilterModel).forEach((column) => {
        if (!quickFilterColumns.value.has(column)) {
            mergedFilterModel[column] = newFilterModel[column]
        }
    })

    // Apply filters and sorts
    const sortModel = sortArray
        ? createSortModel(sortArray)
        : quickFilterSorts.value.length
          ? createSortModel(quickFilterSorts.value)
          : null
    applyFiltersAndRefresh(mergedFilterModel, sortModel)
}

const applyFiltersAndRefresh = (filterModel, sortModel) => {
    debounceManager.cancelAll()

    // Preserve any existing filters that should be kept
    const currentFilterModel = gridApi.value.getFilterModel() || {}
    const newFilterModel = { ...currentFilterModel, ...filterModel }

    gridApi.value.setFilterModel(newFilterModel)

    if (sortModel && sortModel.length > 0) {
        const columnState = gridApi.value.getColumnState()
        columnState.forEach((col) => {
            col.sort = null
            col.sortIndex = null
        })
        // Update sort information in column state
        sortModel.forEach((sort) => {
            const columnToSort = columnState.find((col) => col.colId === sort.colId)
            if (columnToSort) {
                columnToSort.sort = sort.sort
                columnToSort.sortIndex = 0
            }
        })

        // Apply the updated column state
        gridApi.value.applyColumnState({ state: columnState, defaultState: { sort: null } })
    }

    gridApi.value.onFilterChanged('advancedFilter')
    gridApi.value.refreshServerSide({ purge: true })
}

const refreshGridWithFilters = () => {
    debounceManager.gridRefresh(() => {
        // Store the current filter model before refresh
        const currentFilterModel = gridApi.value.getFilterModel()
        // Refresh the grid
        gridApi.value.refreshServerSide({ purge: true })
    })
}

const refreshGrid = refreshGridWithFilters

const createFilterModel = (filters) => {
    if (!gridApi.value || !filters) return {}
    const filterModel = {}

    // Handle product filter (partial string match)
    if (filters.product || filters.name) {
        filterModel.products = {
            filterType: 'text',
            type: 'contains',
            filter: filters.product || filters.name,
        }
    }

    if (filters.breadth) {
        filterModel.claimBreadth = {
            filterType: 'set',
            values: Array.isArray(filters.breadth) ? filters.breadth : [filters.breadth],
        }
    }

    if (filters.validity) {
        filterModel.validity = {
            filterType: 'set',
            values: [filters.validity],
        }
    }

    if (filters.dateRange) {
        filterModel.nextFeeDueDate = {
            filterType: 'date',
            type: 'inRange',
            dateFrom: formatDate(filters.dateRange.from),
            dateTo: formatDate(filters.dateRange.to),
        }
    }

    if (filters.expirationDateRange) {
        filterModel.estimatedExpirationDate = {
            filterType: 'date',
            type: 'inRange',
            dateFrom: formatDate(filters.expirationDateRange.from),
            dateTo: formatDate(filters.expirationDateRange.to),
        }
    }

    if (filters.jurisdiction) {
        filterModel.jurisdiction = {
            filterType: 'text',
            type: 'equals',
            filter: filters.jurisdiction,
        }
    }

    if (filters.legalStatus) {
        filterModel.legalStatus = {
            filterType: 'set',
            values: filters.legalStatus,
        }
    }

    if (filters.prune) {
        filterModel.prune = {
            filterType: 'set',
            values: [filters.prune?.value.toString()],
        }
    }

    if (filters.litigationCount) {
        filterModel.litigationCount = {
            filterType: 'number',
            type: 'greaterThan',
            filter: filters.litigationCount.greaterThanNum,
        }
    }

    return filterModel
}

const createSortModel = (sortArray) => {
    if (!gridApi.value || !sortArray) return

    return sortArray.map((sort) => {
        return {
            colId: sort.column,
            sort: sort.sortDir,
        }
    })
}

const closePatentList = () => {
    emit('close-patent-list')
    patentViewerStore.resetState()
}

const exportPatents = async () => {
    exportLoading.value = true

    try {
        await helperExportPatents({
            patentViewerStore,
            entityStore,
            tableParams: tableParams.value,
            user: user.value,
            logger,
            eventBusEmit: eventBus.emit,
        })
    } catch (err) {
        logger.error(err)
    } finally {
        exportLoading.value = false
    }
}

const exportTablePatents = () => {
    gridApi.value.exportDataAsExcel()
}

const formatDate = (date) => {
    const newDate = new Date(date.split(' ')[0].split('T')[0] + ' 00:00:00')
    newDate.setHours(0, 0, 0, 0)
    const month = String(newDate.getMonth() + 1).padStart(2, '0')
    const day = String(newDate.getDate()).padStart(2, '0')
    const year = newDate.getFullYear()
    return `${year}-${month}-${day} 00:00:00`
}

const debounceManager = {
    debounceTime: 500,

    // Create debounced functions with references we can cancel
    dataFetch: null,
    patentUpdate: null,
    gridRefresh: null,

    // Initialize all debounced functions
    init() {
        this.dataFetch = debounce((params, callback) => callback(params), this.debounceTime)
        this.patentUpdate = debounce(
            (patentId, rowData, callback) => callback(patentId, rowData),
            this.debounceTime
        )
        this.gridRefresh = debounce((callback) => callback(), this.debounceTime)
    },

    // Cancel all pending debounced calls
    cancelAll() {
        this.dataFetch.cancel()
        this.patentUpdate.cancel()
        this.gridRefresh.cancel()
    },
}

const clearFilters = () => {
    if (gridApi.value) {
        sidebarClearFilters(gridApi.value)
    }
}

// Expose the clearFilters method
defineExpose({
    clearFilters,
})

const applyFilters = async (filterChain) => {
    // Clear any debounce jobs to avoid race conditions
    debounceManager.cancelAll()

    if (!gridApi.value) return

    // Reset quick-filter tracking so advanced filters don't collide
    quickFilterColumns.value = new Set()
    quickFilterSorts.value = []
    previousQuickFilter.value = null

    // Guard: no filters – clear model and refresh grid
    if (!filterChain || filterChain.length === 0) {
        gridApi.value.setAdvancedFilterModel(null)
        // Remove manual page reset - AG Grid handles this automatically
        refreshGrid()
        return
    }

    // Filter out any incomplete rows first
    const validFilters = filterChain.filter(
        (f) => f.column && f.value !== null && f.value !== undefined && f.value !== ''
    )

    // Build advanced filter leaves
    const leaves = validFilters.map((f) => {
        if (f.type === 'date') {
            // Helper to convert Date / ISO-with-time to YYYY-MM-DD
            const normalise = (d) => {
                if (!d) return ''
                if (d instanceof Date) {
                    return d.toISOString().split('T')[0]
                }
                if (typeof d === 'string') {
                    return d.includes('T') ? d.split('T')[0] : d
                }
                return String(d)
            }

            if (f.operator === 'inRange') {
                // For date ranges
                let dateFrom, dateTo

                if (Array.isArray(f.value) && f.value.length === 2) {
                    dateFrom = normalise(f.value[0])
                    dateTo = normalise(f.value[1])
                } else if (f.value && typeof f.value === 'object' && f.value.from && f.value.to) {
                    dateFrom = normalise(f.value.from)
                    dateTo = normalise(f.value.to)
                } else {
                    // Fallback: treat as single date
                    dateFrom = normalise(f.value)
                    dateTo = normalise(f.value)
                }

                return {
                    filterType: 'dateString',
                    colId: f.column,
                    type: f.operator,
                    dateFrom,
                    dateTo,
                }
            } else {
                // For single date operations
                return {
                    filterType: 'dateString',
                    colId: f.column,
                    type: f.operator,
                    filter: normalise(f.value),
                }
            }
        } else if (f.type === 'number') {
            // Number filter
            return {
                filterType: 'number',
                colId: f.column,
                type: f.operator || 'equals',
                filter: f.value,
            }
        } else if (f.type === 'set') {
            // Set filter
            return {
                filterType: 'text',
                colId: f.column,
                type: 'equals',
                filter: f.value,
            }
        } else {
            // Text filter
            return {
                filterType: 'text',
                colId: f.column,
                type: f.operator || 'contains',
                filter: f.value,
            }
        }
    })

    // If no valid conditions remain, clear the filters
    if (leaves.length === 0) {
        gridApi.value.setAdvancedFilterModel(null)
        // Remove manual page reset - AG Grid handles this automatically
        refreshGrid()
        return
    }

    // Collect join operators
    const joinOps = validFilters.slice(1).map((f) => (f.join ? f.join.toUpperCase() : 'AND'))

    let advancedFilterModel

    if (leaves.length === 1) {
        advancedFilterModel = leaves[0]
    } else if (new Set(joinOps).size === 1) {
        // All joins are the same
        advancedFilterModel = {
            filterType: 'join',
            type: joinOps[0],
            conditions: leaves,
        }
    } else {
        // Mixed AND / OR
        const buildNested = (index) => {
            if (index === joinOps.length) {
                return leaves[index]
            }
            return {
                filterType: 'join',
                type: joinOps[index],
                conditions: [leaves[index], buildNested(index + 1)],
            }
        }
        advancedFilterModel = buildNested(0)
    }

    // Apply the advanced filter model
    gridApi.value.setAdvancedFilterModel(advancedFilterModel)
    gridApi.value.onFilterChanged('advancedFilter')
}

function applySeedStringFilter(retryCount = 0) {
    const seed = route.params?.seedString || route.query?.seedString

    if (seedFilterApplied.value) return
    if (!seed || typeof seed !== 'string' || seed.trim() === '') return

    // Check if all dependencies are ready
    if (!advancedFiltering.value || !gridApi.value) {
        if (retryCount < 5) {
            setTimeout(() => {
                applySeedStringFilter(retryCount + 1)
            }, 200)
        }
        return
    }

    const filterChain = [
        {
            id: Date.now(),
            column: 'patentId',
            operator: 'contains',
            value: seed,
            type: 'text',
            join: 'and',
        },
    ]
    prefillFilters.value = filterChain
    applyFilters(filterChain)
    seedFilterApplied.value = true
}

watch(
    () => route.fullPath,
    (newVal, oldVal) => {
        if (newVal !== oldVal && newVal.includes('seedString')) {
            seedFilterApplied.value = false
            enableAdvancedFilter()
            applySeedStringFilter()
        }
    }
)

watch(
    () => route.query.seedString,
    (newSeed, oldSeed) => {
        if (newSeed !== oldSeed) {
            seedFilterApplied.value = false
            if (newSeed && gridApi.value) {
                enableAdvancedFilter()
                applySeedStringFilter()
            }
        }
    }
)

// Watch for when advanced filtering flag becomes available
watch(
    () => advancedFiltering.value,
    (newVal, oldVal) => {
        // Only apply if flag changed from false to true and grid is ready
        if (newVal && !oldVal && gridApi.value) {
            nextTick(() => {
                setTimeout(() => {
                    applySeedStringFilter()
                }, 200)
            })
        }
    }
)
</script>

<style lang="scss" scoped>
:deep(.not-pruned) path {
    fill: var(--color-stone-300) !important;
}

:deep(.pruned) path {
    fill: var(--color-success) !important;
}

.patent-list {
    &.no-action-bar {
        :deep(.ag-root) {
            padding-top: 0 !important;
        }

        .table-holder {
            margin-top: 0 !important;
        }

        .root-actions {
            top: 0 !important;
        }

        .action-bar {
            top: 0 !important;
        }
    }

    .table-holder {
        position: relative;
        margin-top: -48px;
        padding: 0 16px !important;
    }

    .root-actions {
        position: relative;
        top: 48px;
        z-index: 2;
    }

    .action-bar {
        height: 50px;
        position: relative;
        top: 48px;
        z-index: 1;
        border-bottom: 1px solid var(--color-stone-300);

        &.sidebar-open {
            margin-left: 275px;
        }

        .divide-border {
            border-left: 2px solid var(--color-stone-300);
        }
    }

    :deep(.a-modal-close) {
        z-index: 11 !important;
    }

    &.inline {
        position: static !important;
        left: 0 !important;
        top: 0 !important;
        z-index: 1 !important;

        transform: translate(0, 0) !important;

        .table-holder {
            background: var(--color-stone-50);
            z-index: 10;
            padding: 0 !important;
        }

        :deep(.a-modal-close) {
            display: none !important;
            pointer-events: none !important;
        }

        :deep(.background) {
            display: none !important;
        }

        :deep(.aon-container) {
            height: 100% !important;
        }

        :deep(.aon-row) {
            height: 100% !important;
        }

        :deep(.aon-col) {
            height: 100% !important;
        }

        :deep(.container) {
            box-shadow: none !important;
            transform: none !important;
            left: 0 !important;
            top: 0 !important;

            .patent-list-header {
                display: none !important;
            }
        }

        :deep(.grid-cols-10) {
            padding: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            flex: 0 0 100% !important;
        }
    }

    .bulk-action {
        padding: 10px;
        padding-bottom: 8px;
        border-radius: var(--border-radius, 8px);
        cursor: pointer;

        transition: all 0.3s ease-in-out;

        &:hover {
            background: var(--color-stone-200);
        }
    }

    .total-holder {
        position: absolute;
        bottom: 15px;
        left: 20px;
    }

    .fees-saved-header {
        border-left: 2px solid var(--color-stone-400);
    }

    &.absolute {
        position: absolute !important;
    }

    z-index: 11 !important;

    .clear-filters {
        min-width: 144px;
        border-radius: var(--border-radius-md);
        border: 1px solid transparent;
        transition: all 0.3s ease-in-out;

        &:hover {
            border: 1px solid var(--color-stone-400);
            background: var(--color-moat-50);
        }
    }

    :deep(.a-modal-close) {
        background: var(--color-stone-200);
        transform: scale(1.2) !important;
        top: 20px;
        right: 20px;

        svg {
            color: var(--color-stone-700) !important;
        }
    }

    :deep(.offset-1) {
        margin-left: 0 !important;
    }

    :deep(.aon-col) {
        padding: 10px !important;
    }

    :deep(.aon-container) {
        max-width: none !important;
    }

    :deep(.grid-cols-10) {
        flex: 0 0 100% !important;
        max-width: 100% !important;
    }

    :deep(.container) {
        width: 100% !important;
        height: 100% !important;
        padding: 0 !important;
        max-height: none !important;
        box-shadow: 0px 0px 35px 10px rgba(0, 0, 0, 0.35) !important;
    }

    :deep(.modal-content) {
        height: 100% !important;
    }

    :deep(.background) {
        width: 110%;
        height: 110%;
        top: -3px;
        left: -3px;
    }

    .patent-list-header {
        min-height: 75px;
        width: 100%;

        .source-display {
            height: 100%;
        }
    }

    .divide {
        width: 2px;
        height: 20px;
        background-color: var(--color-stone-200);
    }
}
</style>
